import type { PuzzleType } from '@arrow/puzzle-schema';

import { type NibPalette, type NibPose, nibPose } from '../brand/art.ts';

/** Nib is printed in the theme's ink (tokens.css: paper by day, a light body at night). */
const PALETTE: NibPalette = { ink: 'var(--nib-ink)', stock: 'var(--nib-face)', ver: 'var(--nib-tail)' };

const POSES: NibPose[] = ['idle', 'happy', 'cheeky', 'ruffled', 'cheer', 'lookup', 'worried', 'dive'];
const ART = Object.fromEntries(POSES.map((p) => [p, nibPose(p, PALETTE)])) as Record<NibPose, string>;
/** Seasonal hats, by puzzle type: a witch's hat (Halloween), a Santa hat (Christmas). */
const WITCH_ART = Object.fromEntries(POSES.map((p) => [p, nibPose(p, PALETTE, { hat: 'witch' })])) as Record<NibPose, string>;
const SANTA_ART = Object.fromEntries(POSES.map((p) => [p, nibPose(p, PALETTE, { hat: 'santa' })])) as Record<NibPose, string>;
const HAT_ART: Partial<Record<PuzzleType, Record<NibPose, string>>> = { halloween: WITCH_ART, christmas: SANTA_ART };

/**
 * Nib, the Sparrow character (docs/mockups/sparrow.html, Nib v3), in one of the mock's
 * poses. Used sparingly: one per screen at most, never inside the grid and never while
 * typing (Today, the DONE. poster, the challenge landing, empty and error states).
 *
 * Decorative by default (`aria-hidden`); pass `label` where Nib carries meaning.
 * `blink` (idle only): a rare, tiny blink; static under reduced motion (screens.css).
 */
export function Nib({
	pose,
	className,
	label,
	blink,
	onClick,
	type,
}: {
	pose: NibPose;
	className?: string;
	label?: string;
	blink?: boolean;
	/** A quiet tap target (Stats: the dev-mode secret). Not a control: no role, no focus. */
	onClick?: () => void;
	/** The puzzle on screen's type: a Halloween puzzle puts him in his witch's hat. */
	type?: PuzzleType | undefined;
}) {
	const cls = ['nib', `nib-${pose}`, blink ? 'nib-blink' : '', className ?? ''].filter(Boolean).join(' ');
	const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const, focusable: 'false' as const };
	return (
		<svg
			className={cls}
			viewBox="0 0 120 120"
			data-pose={pose}
			data-tappable={onClick ? '' : undefined}
			onClick={onClick}
			{...a11y}
			dangerouslySetInnerHTML={{ __html: ((type && HAT_ART[type]) || ART)[pose] }}
		/>
	);
}
